/**
 * apiClient.js — die EINZIGE Datei, die das Fehler-Wire-Format des Servers kennt:
 * { error: { code, message, details? } }. Jede Nicht-2xx-Antwort und jeder Netzwerkfehler wird
 * ein ApiClientError; Anzeige-Entscheidungen trifft ausschließlich `errorMessage()`
 * (api/errorMessage.js), Logik prüft `err.code`, nie Text.
 * Spec: docs/superpowers/specs/2026-08-25-error-handling-system.md §4.1
 */
import { ApiClientError } from './ApiClientError';

function parseErrorBody(text) {
  if (!text) return { code: null, message: '', details: undefined };
  try {
    const parsed = JSON.parse(text);
    const e = parsed?.error;
    if (e && typeof e === 'object') {
      return {
        code: typeof e.code === 'string' ? e.code : null,
        message: typeof e.message === 'string' ? e.message : '',
        details: e.details,
      };
    }
  } catch {}
  return { code: null, message: '', details: undefined };
}

async function toApiClientError(resp) {
  let text = '';
  try { text = await resp.text(); } catch {}
  return new ApiClientError({ status: resp.status, ...parseErrorBody(text) });
}

export async function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem('authToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let resp;
  try {
    resp = await fetch(url, { ...options, headers });
  } catch {
    throw new ApiClientError({ status: 0, code: 'NETWORK', message: '' });
  }
  if (resp.status === 401) throw await toApiClientError(resp);
  return resp;
}

export async function json(url, options = {}) {
  const headers = { Accept: 'application/json', ...(options.headers || {}) };
  const resp = await fetchWithAuth(url, { ...options, headers });
  if (!resp.ok) throw await toApiClientError(resp);
  return resp.json();
}

// Axios-like API client wrapper for convenience
export const apiClient = {
  get: async (url, config = {}) => {
    const result = await json(url, config);
    return { data: result, status: 200 };
  },
  post: async (url, data, config = {}) => {
    const result = await json(url, {
      ...config,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(config.headers || {}) },
      body: JSON.stringify(data),
    });
    return { data: result, status: 200 };
  },
  put: async (url, data, config = {}) => {
    const result = await json(url, {
      ...config,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(config.headers || {}) },
      body: JSON.stringify(data),
    });
    return { data: result, status: 200 };
  },
  patch: async (url, data, config = {}) => {
    const result = await json(url, {
      ...config,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(config.headers || {}) },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    return { data: result, status: 200 };
  },
  delete: async (url, config = {}) => {
    const result = await json(url, { ...config, method: 'DELETE' });
    return { data: result, status: 200 };
  },
};
